import React from 'react';
import { Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import { StyledFlexDiv, StyledInput, StyledSelect } from './styles';
import { CpscItemType } from '__generated__/graphql';

export interface CpscReferencePgaMessageSetProps {
  localNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

const ITEM_TYPE_OPTIONS = [
  { value: CpscItemType.PRODUCT, label: 'Product' },
  { value: CpscItemType.COMPONENT, label: 'Component' },
];

const createLabelWithTooltip = (label: string, tooltip: string) => (
  <>
    {label}{' '}
    <Tooltip title={tooltip}>
      <InfoCircleOutlined />
    </Tooltip>
  </>
);

const CpscReferencePgaMessageSet: React.FC<CpscReferencePgaMessageSetProps> = ({
  localNamePath,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();
  const productRegistryPath = [...localNamePath, 'productRegistryReference'];

  return (
    <>
      {/* Row 1: Item Type and Description */}
      <StyledFlexDiv>
        <FormItem
          name={[...localNamePath, 'itemType']}
          label="Item Type"
          $itemSize={theme.size.short}
          $readOnly={readOnly}
          rules={[{ required: true }]}
          validateTrigger="onBlur"
        >
          <StyledSelect
            options={ITEM_TYPE_OPTIONS}
            disabled={disabled || readOnly}
            placeholder="Select item type"
          />
        </FormItem>
        <FormItem
          name={[...localNamePath, 'description']}
          label="Description"
          $itemSize={theme.size.medium}
          $readOnly={readOnly}
        >
          <StyledInput
            disabled={disabled || readOnly}
            maxLength={70}
            placeholder="Enter description for this item"
          />
        </FormItem>
      </StyledFlexDiv>

      {/* Row 2: Product ID and Version ID */}
      <StyledFlexDiv>
        <FormItem
          name={[...productRegistryPath, 'productId']}
          label={createLabelWithTooltip(
            'CPSC Product Registry ID',
            'The product ID that is used in the CPSC Product Registry. 18 characters.',
          )}
          $itemSize={theme.size.short}
          $readOnly={readOnly}
          rules={[{ required: true }]}
          validateTrigger="onBlur"
        >
          <StyledInput disabled={disabled || readOnly} maxLength={18} />
        </FormItem>
        <FormItem
          name={[...productRegistryPath, 'versionId']}
          label={createLabelWithTooltip(
            'CPSC Product Registry ID version',
            'The product ID version that is used in the CPSC Product Registry. 3 characters.',
          )}
          $itemSize={theme.size.short}
          $readOnly={readOnly}
          rules={[{ required: true }]}
          validateTrigger="onBlur"
        >
          <StyledInput disabled={disabled || readOnly} maxLength={3} />
        </FormItem>
      </StyledFlexDiv>

      {/* Row 3: Certifier ID */}
      <FormItem
        name={[...productRegistryPath, 'certifierId']}
        label={createLabelWithTooltip(
          'LCPO Number - Certifier ID',
          'Certifier ID for the product registry. 23 characters.',
        )}
        $itemSize={theme.size.short}
        $readOnly={readOnly}
        rules={[{ required: true }]}
        validateTrigger="onBlur"
      >
        <StyledInput disabled={disabled || readOnly} maxLength={23} />
      </FormItem>
    </>
  );
};

export default CpscReferencePgaMessageSet;
